import { useCallback, useEffect, useMemo, useState } from 'react';
import { logError } from 'utils/helpers';

declare global {
  interface Window {
    POWERREVIEWS: any;
  }
}

// src for pulling in PowerReviews base widget that will have our style overrides applied
const POWER_REVIEWS_SCRIPT_SRC = '//ui.powerreviews.com/stable/4.0/ui.js';
// selectors needed for base widget overrides
const WOULD_RECOMMEND_SELECTOR = '.pr-rd-bottomline';
const SORT_REVIEWS_SELECTOR = '#pr-rd-sort-by';
const NEXT_PAGE_SELECTOR = '.pr-rd-pagination-btn';
const WOULD_RECOMMEND_TEXT = 'Would recommend to a friend';
const SHOW_RECOMMENDATION_CLASS = 'showRecommendation';
export const REVIEW_DISPLAY_NODE = 'pr-reviewdisplay';
const WRITE_A_REVIEW_SELECTOR = '.pr-snippet-write-review-link';

const updateRecommendSection = (node: Element) => {
  const text = node.getElementsByTagName('span')[1];
  if (text.innerText.includes('No')) {
    node.remove();
  } else {
    text.innerText = WOULD_RECOMMEND_TEXT;
    node.classList.add(SHOW_RECOMMENDATION_CLASS);
  }
};

const appendScript = (src: string) =>
  new Promise(function (resolve, reject) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', function (e) {
      resolve(e);
    });
    script.addEventListener('error', function (e) {
      reject(e);
    });
    document.body?.appendChild(script);
  });

// Function to clean URLs by removing tracking parameters
// This is used to prevent Power Reviews from appending tracking parameters to the URL when the user clicks on the next or previous page of reviews.
// https://everlyhealth.atlassian.net/browse/IN2-2236
const cleanUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);

    const preservedParams = new URLSearchParams();
    // Keep only those starting with 'pr_' for Power Reviews
    urlObj.searchParams.forEach((value, key) => {
      if (key.startsWith('pr_')) {
        preservedParams.set(key, value);
      }
    });

    // Replace all parameters with only the preserved ones
    urlObj.search = preservedParams.toString();

    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL as a string
    return url;
  }
};

const updatePaginationLinks = () => {
  document.querySelectorAll(NEXT_PAGE_SELECTOR).forEach((node) => {
    if (node instanceof HTMLAnchorElement) {
      node.href = cleanUrl(node.href);
      // Use nofollow since these are modal pagination links
      node.rel = 'nofollow';
    }
  });
};

const handleNextPageClick = () => {
  setTimeout(() => {
    document
      .querySelectorAll(WOULD_RECOMMEND_SELECTOR)
      .forEach((node) => updateRecommendSection(node));
    updatePaginationLinks();
  }, 300);
};

const handleSortChange = () => {
  setTimeout(() => {
    document
      .querySelectorAll(WOULD_RECOMMEND_SELECTOR)
      .forEach((node) => updateRecommendSection(node));
    // Clean URLs and update rel attributes after sorting
    updatePaginationLinks();
  }, 300);
};

const onRender = (config: any) => {
  if (config.component === 'ReviewDisplay') {
    document
      .querySelectorAll(WOULD_RECOMMEND_SELECTOR)
      .forEach((node) => updateRecommendSection(node));

    // Clean pagination URLs and add pagination rel attributes
    updatePaginationLinks();

    // Remove unnecessary DOM elements
    document
      .querySelectorAll(WRITE_A_REVIEW_SELECTOR)
      .forEach((node) => node.remove());

    // setting event listener to update DOM when user paginates
    // the powerreviews widget requests out to an external API and does not expose
    // a callback for when that request resolves, so we need to wrap them in a setTimeout (cringe)
    // Update pagination event listeners. This happens when the user clicks on the next or previous page of reviews.
    document
      .querySelectorAll(NEXT_PAGE_SELECTOR)
      .forEach((node) => node.addEventListener('click', handleNextPageClick));

    document
      .querySelector(SORT_REVIEWS_SELECTOR)
      ?.addEventListener('change', handleSortChange);
  }
};

const PR_MERCHANT_ID = process.env.POWER_REVIEWS_MERCHANT_ID;
const PR_MERCHANT_GROUP_ID = process.env.POWER_REVIEWS_MERCHANT_GROUP_ID;
const PR_API_KEY = process.env.POWER_REVIEWS_API_KEY;

const usePowerReviews = (productId: string) => {
  const [prLoaded, setPrLoaded] = useState<boolean>(false);

  const powerReviewsOptions = useMemo(
    () => ({
      api_key: PR_API_KEY,
      locale: 'en_US',
      merchant_group_id: PR_MERCHANT_GROUP_ID,
      merchant_id: PR_MERCHANT_ID,
      page_id: productId,
      // prevent PowerReviews from appending parameters to the URL when the user clicks on the next or previous page of reviews.
      urlParamKeys: [],
      // Disable Product Schema generation from Power Reviews as we generate
      ENABLE_CLIENT_SIDE_STRUCTURED_DATA: false,
      // once powerreviews widget has been rendered, we execute this callback
      // to update styles, and setup event listeners so that they update when the user
      // changes the sort or paginates to another page
      // https://help.powerreviews.com/Content/Platform/JavaScript%20Reference%20Guide.htm#Callback
      on_render: onRender,
    }),
    [productId],
  );

  const preloadPowerReviews = useCallback(() => {
    window.POWERREVIEWS?.display?.render({
      ...powerReviewsOptions,
      components: { ReviewDisplay: 'preloaded-pr' },
    });
  }, [powerReviewsOptions]);

  const renderPowerReviews = useCallback(() => {
    window.POWERREVIEWS?.display?.render({
      components: {
        ReviewDisplay: REVIEW_DISPLAY_NODE,
      },
      ...powerReviewsOptions,
    });
  }, [powerReviewsOptions]);

  useEffect(() => {
    if (!prLoaded) {
      // preload power reviews on page load
      // TODO: revisit our review vendor and/or how we render these. several things broken here.
      const loadPowerReviews = appendScript(POWER_REVIEWS_SCRIPT_SRC);
      loadPowerReviews
        .then(() => {
          preloadPowerReviews();
          setPrLoaded(true);
        })
        .catch((e: any) =>
          logError(
            `There was an issue loading the powerreviews script from CDN: ${e}`,
            {
              component: 'ReviewsContainer',
              function: 'useEffect',
            },
          ),
        );
    }
  }, [prLoaded, preloadPowerReviews]);

  return {
    renderPowerReviews,
  };
};

export default usePowerReviews;
